import React, { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'

const MOBILE_HERO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_design-osuna/artifacts/a4ddb99x_ChatGPT%20Image%2022%20sept%202026%2C%2001_07_01.png"
import { HERO } from '../constants/testIds'
import { useSiteContent } from '../contexts/SiteContentContext'

const BF = 'Anton, sans-serif'
const MONO = 'JetBrains Mono, monospace'

function SlideIn({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  )
}



export default function Hero() {
  const containerRef = useRef(null)
  const { scrollY } = useScroll()
  const { content } = useSiteContent()
  const hero = content.hero

  // Detectar móvil para usar imagen específica
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const photo1Src = isMobile ? MOBILE_HERO_URL : hero.photo1_url

  // Parallax layers — photo2 visible early while hero is on-screen
  const photo1Y       = useTransform(scrollY, [0, 600], ['0%', '12%'])
  const photo2Y       = useTransform(scrollY, [0, 600], ['4%', '-4%'])
  const photo1Opacity = useTransform(scrollY, [0, 180, 380], [1, 0.2, 0])
  const photo2Opacity = useTransform(scrollY, [30, 160, 350], [0, 0.75, 1])
  const textY         = useTransform(scrollY, [0, 600], ['0%', '-7%'])
  const heroOpacity   = useTransform(scrollY, [220, 600], [1, 0])

  return (
    <section
      ref={containerRef}
      data-testid={HERO.section}
      className="relative h-dvh overflow-hidden bg-black"
    >
      {/* ── Photo 2 — aparece al empezar a hacer scroll ── */}
      <motion.div className="absolute inset-0" style={{ y: photo2Y, opacity: photo2Opacity }}>
        <img
          src={hero.photo2_url}
          alt="José Osuna — visión urbana"
          className="w-full h-full object-cover object-center"
          style={{ transform: 'scale(1.08)' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #000 0%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.3) 80%, transparent 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #000 0%, rgba(0,0,0,0.4) 20%, transparent 55%)' }} />
      </motion.div>

      {/* ── Photo 1 (perfil dorado) — visible al inicio ── */}
      <motion.div className="absolute inset-0" style={{ y: photo1Y, opacity: photo1Opacity }}>
        <img
          src={photo1Src}
          alt="José Osuna — Diseñador Web"
          data-testid={HERO.photo}
          className="w-full h-full object-cover"
          style={{ transform: 'scale(1.1)', objectPosition: isMobile ? 'center center' : 'center center' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, #000 0%, #000 18%, rgba(0,0,0,0.6) 45%, rgba(0,0,0,0.1) 70%, transparent 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #000 0%, rgba(0,0,0,0.45) 20%, transparent 55%)' }} />
      </motion.div>

      {/* Top header gradient */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 22%)', zIndex: 1 }} />

      {/* ── Giant text overlay ── */}
      <motion.div
        className="absolute bottom-[12%] md:bottom-[6%] left-0 pl-5 md:pl-10 xl:pl-16"
        style={{ y: textY, opacity: heroOpacity, zIndex: 2 }}
      >
        <motion.div
          className="flex items-center gap-4 mb-16"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.9 }}
        >
          <div className="w-8 h-px bg-amber-400" />
          <span className="text-xs tracking-[0.35em] text-amber-400 uppercase" style={{ fontFamily: MONO }}>
            {hero.subtitle}
          </span>
        </motion.div>

        <SlideIn delay={0.55}>
          <h1
            data-testid={HERO.textLine1}
            className="font-normal uppercase text-white select-none"
            style={{ fontFamily: BF, fontSize: 'clamp(60px, 14vw, 240px)', letterSpacing: '0.01em', lineHeight: 1.05 }}
          >
            {hero.line1}
          </h1>
        </SlideIn>

        <SlideIn delay={0.72}>
          <h1
            data-testid={HERO.textLine2}
            className="font-normal uppercase select-none"
            style={{ fontFamily: BF, fontSize: 'clamp(60px, 14vw, 240px)', letterSpacing: '0.01em', lineHeight: 0.9, color: '#F5A623', marginLeft: 'clamp(20px, 4vw, 70px)', whiteSpace: 'nowrap' }}
          >
            {hero.line2}
          </h1>
        </SlideIn>
      </motion.div>

      {/* Right descriptor */}
      <motion.div
        className="absolute right-5 md:right-14"
        style={{ bottom: '12%', opacity: heroOpacity, zIndex: 2 }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <p className="text-right text-white/40 leading-loose" style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.2em' }}>
          DISEÑO QUE CONVIERTE<br />VISITAS EN CLIENTES
        </p>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        data-testid={HERO.scrollIndicator}
        onClick={() => document.querySelector('#sobre-mi')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 bg-transparent border-0"
        style={{ zIndex: 2 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
      >
        <motion.div className="w-px h-12 bg-white/20 origin-top"
          animate={{ scaleY: [1, 0.3, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-white/30 uppercase" style={{ fontFamily: MONO, fontSize: '9px', letterSpacing: '0.3em' }}>Scroll</span>
      </motion.button>
    </section>
  )
}
